/**
 * @description Strips IEEE 754 floating-point noise (e.g. 0.100000000000008 -> 0.1)
 * by keeping only the first `sigFigs` significant digits. Uses significant figures
 * rather than fixed decimals so it works across magnitudes (tiny and large values).
 * Apply at arithmetic boundaries, not on raw untouched inputs.
 * @param value number to clean
 * @param sigFigs significant digits to keep (default 12 — well below the ~15-17 a
 * double carries, so real precision is preserved while noise in the last digits is removed)
 */
export const cleanFloat = (value: number, sigFigs = 12): number => {
  if (!Number.isFinite(value)) return value;
  return Number.parseFloat(value.toPrecision(sigFigs));
};

/**
 * @description Formats a numeric value for DISPLAY only — never mutate stored state with it.
 * When `decimals` is provided the value is snapped to that many decimal places (fixed
 * formatting for readability); otherwise floating-point noise is stripped via cleanFloat.
 * Non-numeric input is returned unchanged so callers can pass mixed values safely.
 * @param value value to display
 * @param decimals optional fixed decimal places
 */
export const formatNumber = (value: number, decimals?: number): number => {
  if (!Number.isFinite(value)) return value;
  return decimals !== undefined ? Number(value.toFixed(decimals)) : cleanFloat(value);
};
